/**
 * Belt-and-braces recovery: if a signed-in user has no users/{uid}
 * doc — meaning the original signup's call to the
 * `ensure_user_profile` callable failed somehow (network blip,
 * function cold-start error) — we silently re-run it. The callable
 * is idempotent server-side, so this is safe.
 *
 * # Important
 * - We block ONLY on the initial snapshot ('loading') so we don't
 *   render an unauthenticated-looking page while Firestore figures
 *   out whether the doc exists.
 * - On 'missing' we kick off recovery in the background and render
 *   the Outlet immediately. Recovery + the listener can take many
 *   seconds; downstream pages (RootRedirect, /welcome) have their
 *   own loading states and don't need users/{uid} to render.
 */

import { Outlet } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { useUserProfile } from '@/hooks/useUserProfile';
import { ensureUserProfileCallable } from '@/lib/auth';
import { isWithinSignupWindow } from '@/lib/signupRecoveryWindow';
import { Spinner } from '@/components/ui/Spinner';
import { log } from '@/lib/logger';

export function EnsureUserProfile() {
  const auth = useAuth();
  const uid = auth.state === 'signedIn' ? auth.user.uid : null;
  const profile = useUserProfile(uid);
  const recoveryAttempted = useRef(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);

  useEffect(() => {
    if (
      auth.state === 'signedIn' &&
      profile.state === 'missing' &&
      !recoveryAttempted.current &&
      // Complement to useUserProfile's fromCache guard: that covers cold
      // reloads; this covers a fresh signup, where the server briefly reports
      // the doc missing before the awaited ensure_user_profile commits. The
      // signup's own call is creating it, so recovery is redundant within the
      // window — suppress it and let the listener catch up.
      !isWithinSignupWindow(auth.user.metadata.creationTime, Date.now())
    ) {
      recoveryAttempted.current = true;
      log.info('user profile missing — running silent recovery', {}, { sendToCloud: true });
      ensureUserProfileCallable({
        displayName: auth.user.displayName ?? undefined,
        locale: navigator.language || 'en-GB',
      }).catch((e: unknown) => {
        setRecoveryError(e instanceof Error ? e.message : 'Recovery failed.');
        recoveryAttempted.current = false;
      });
    }
  }, [auth, profile]);

  // Only block on the very first snapshot resolution. After that,
  // whether the doc is present or missing, render the Outlet —
  // downstream pages handle their own loading and recovery runs
  // in the background.
  if (uid && profile.state === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-ink-muted">
          <Spinner />
          <span className="text-sm">
            {recoveryError ? `Could not finish signup: ${recoveryError}` : 'Loading your account…'}
          </span>
        </div>
      </div>
    );
  }

  return <Outlet />;
}
